import { useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface LogoUploadProps {
  userId: string;
  currentLogoUrl?: string | null;
  onLogoChange: (url: string | null) => void;
}

export default function LogoUpload({ userId, currentLogoUrl, onLogoChange }: LogoUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(currentLogoUrl ?? null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, SVG, WebP).');
      return;
    }

    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Logo must be under 5MB.');
      return;
    }

    setError(null);
    setUploading(true);

    // Show local preview immediately
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);

    try {
      const ext = file.name.split('.').pop() ?? 'png';
      const fileName = `logos/${userId}/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('ad-images')
        .upload(fileName, file, { contentType: file.type, upsert: false });

      if (uploadError) {
        setError('Upload failed. Please try again.');
        setPreview(currentLogoUrl ?? null);
        setUploading(false);
        return;
      }

      const { data: urlData } = supabase.storage
        .from('ad-images')
        .getPublicUrl(fileName);

      const publicUrl = urlData?.publicUrl ?? null;
      setPreview(publicUrl);
      onLogoChange(publicUrl);
    } catch {
      setError('Upload failed. Please try again.');
      setPreview(currentLogoUrl ?? null);
    } finally {
      setUploading(false);
      // Reset input so same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onLogoChange(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="flex items-start gap-4">
      {/* Preview / Placeholder */}
      <div className="shrink-0">
        {preview ? (
          <div className="relative w-20 h-20 rounded-xl border border-stone-200 bg-stone-50 overflow-hidden">
            <img
              src={preview}
              alt="Business logo"
              className="w-full h-full object-contain p-1"
            />
            {uploading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-orange-500 text-lg" />
              </div>
            )}
          </div>
        ) : (
          <div className="w-20 h-20 rounded-xl border-2 border-dashed border-stone-200 bg-stone-50 flex flex-col items-center justify-center gap-1">
            {uploading ? (
              <i className="ri-loader-4-line animate-spin text-orange-400 text-xl" />
            ) : (
              <>
                <i className="ri-image-line text-stone-300 text-2xl" />
                <span className="text-xs text-stone-400">Logo</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-stone-200 bg-white text-stone-600 hover:border-orange-300 hover:text-orange-500 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-upload-2-line text-xs" />
            {preview ? 'Replace Logo' : 'Upload Logo'}
          </button>
          {preview && !uploading && (
            <button
              type="button"
              onClick={handleRemove}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-stone-200 bg-white text-red-400 hover:border-red-300 hover:text-red-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line text-xs" />
              Remove
            </button>
          )}
        </div>
        <p className="text-xs text-stone-400 mt-2 leading-relaxed">
          PNG, JPG, SVG or WebP · Max 5MB · Used as a visual reference during generation (optional)
        </p>
        {error && (
          <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
            <i className="ri-error-warning-line" /> {error}
          </p>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}
